/**
 * src/utils/skoUrlResolver.js
 * Comprehensive URL resolving, file type detection, and SKO Public File helpers
 */

export const SKO_FILES_BASE = 'https://skoservice.akawatmor.com/files/'

/**
 * Iteratively decodes a string until all percent-encoded layers (%2520 -> %20 -> ' ') are resolved.
 * @param {string} str
 * @returns {string}
 */
export function fullyDecode(str) {
  if (!str || typeof str !== 'string') return ''
  let prev = str
  let curr = str
  try {
    for (let i = 0; i < 5; i++) {
      curr = decodeURIComponent(prev)
      if (curr === prev) break
      prev = curr
    }
  } catch {
    return prev
  }
  return curr
}

/**
 * Normalizes a media URL so all path segments are cleanly single-encoded.
 * Handles double-encoded (%2520), unencoded spaces, Thai/Unicode, and '#' -> '%23'.
 *
 * @param {string} urlStr
 * @returns {string}
 */
export function normalizeMediaUrl(urlStr) {
  if (!urlStr || typeof urlStr !== 'string') return ''
  let str = urlStr.trim()

  if (str.startsWith('blob:')) return str

  // Separate query parameters (?query) if any
  const qIndex = str.indexOf('?')
  let queryString = ''
  if (qIndex !== -1) {
    queryString = str.slice(qIndex)
    str = str.slice(0, qIndex)
  }

  // Separate origin if absolute URL (http://... or https://...)
  const originMatch = str.match(/^([a-zA-Z][a-zA-Z0-9+\-.]*:\/\/[^\/]+)(.*)$/)
  let origin = ''
  let path = str
  if (originMatch) {
    origin = originMatch[1]
    path = originMatch[2]
  }

  // Process each path segment: fully decode first, then encode cleanly
  const segments = path.split('/').map(seg => {
    if (!seg) return seg
    const clean = fullyDecode(seg)
    return encodeURIComponent(clean)
  })

  return origin + segments.join('/') + queryString
}

/**
 * Resolves a query URL parameter into a full, browser-ready URL.
 * - If url is already blob:, returns as-is.
 * - If url is http(s)://, normalizes it cleanly.
 * - If url is a relative path (e.g. 'Audio%2Ftest.mp3' or 'Video/sample.mp4'),
 *   prepends SKO_FILES_BASE with properly encoded segments.
 *
 * @param {string} inputUrl
 * @returns {string}
 */
export function resolveFullUrl(inputUrl) {
  if (!inputUrl || typeof inputUrl !== 'string') return ''
  const str = inputUrl.trim()

  if (str.startsWith('blob:')) {
    return str
  }

  const isDev = Boolean(typeof import.meta !== 'undefined' && import.meta.env?.DEV)
  const skoPattern = /^https?:\/\/(?:skoservice\.akawatmor\.com|skoservice2\.akawatmor\.com|ddc1\.akawatmor\.com:56260|legacyservice\.akawatmor\.com:56260)\/files\/?/i

  if (isDev && skoPattern.test(str)) {
    const rel = str.replace(skoPattern, '')
    const decoded = fullyDecode(rel).replace(/^\/+/, '')
    const segments = decoded.split('/').map(encodeURIComponent)
    return `/files/${segments.join('/')}`
  }

  if (str.startsWith('/files/')) {
    return str
  }

  if (str.startsWith('http://') || str.startsWith('https://')) {
    return normalizeMediaUrl(str)
  }

  // Relative path from SKO Public Files base
  const decoded = fullyDecode(str).replace(/^\/+/, '')
  const segments = decoded.split('/').map(encodeURIComponent)

  if (isDev) {
    return `/files/${segments.join('/')}`
  }

  return `${SKO_FILES_BASE}${segments.join('/')}`
}

/**
 * Converts a full SKO file URL into a clean relative encoded path for query param.
 * Example: https://skoservice.akawatmor.com/files/Audio/test.mp3 -> Audio%2Ftest.mp3
 *
 * @param {string} fullOrRelativeUrl
 * @returns {string}
 */
export function formatToRelativePath(fullOrRelativeUrl) {
  if (!fullOrRelativeUrl) return ''
  const str = fullOrRelativeUrl.trim()

  if (str.startsWith('blob:')) return str

  // If it's on SKO public files domain (or any legacy skoservice domain, or dev /files/)
  const skoPattern = /^(?:https?:\/\/(?:skoservice\.akawatmor\.com|skoservice2\.akawatmor\.com|ddc1\.akawatmor\.com:56260|legacyservice\.akawatmor\.com:56260)|\/files)?\/files\/?/i
  if (skoPattern.test(str)) {
    const rel = str.replace(skoPattern, '')
    const decoded = fullyDecode(rel)
    return encodeURIComponent(decoded)
  }

  return normalizeMediaUrl(str)
}


/**
 * Sanitizes input URL or relative path:
 * 1. Fully decodes percent-encoded sequences (%2F -> /, %20 -> space, Thai Unicode)
 * 2. Normalizes Windows backslashes '\' to '/'
 * 3. Strips URL query parameters (?...)
 * 4. Removes SKO origin prefix if present to get clean relative path
 *
 * @param {string} urlStr
 * @returns {string}
 */
export function sanitizePath(urlStr) {
  if (!urlStr || typeof urlStr !== 'string') return ''
  let str = urlStr.trim()
  if (!str) return ''

  if (str.startsWith('blob:')) {
    return str
  }

  const originMatch = str.match(/^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\/[^\/]+(\/.*)?$/)
  if (originMatch) {
    str = originMatch[1] || '/'
  }

  const qIdx = str.indexOf('?')
  if (qIdx !== -1) {
    str = str.substring(0, qIdx)
  }

  str = fullyDecode(str)
  str = str.replace(/\\/g, '/')
  str = str.replace(/^\/files\/?/i, '')
  str = str.replace(/^\/+/, '')

  return str
}

/**
 * 1. Extracts the "real" decoded filename with extension from a URL or relative path.
 * Returns null if the path represents a directory or is empty.
 * Example: 'Audio%2FMusic%2Fธงไชย%2F08 หมอก.m4a?type=json' -> '08 หมอก.m4a'
 * Example: '/ห#ห#/#ก#ก.m4a' -> '#ก#ก.m4a'
 *
 * @param {string} urlStr
 * @returns {string|null}
 */
export function extractRealFileName(urlStr) {
  if (!urlStr || typeof urlStr !== 'string') return null
  if (urlStr.startsWith('blob:')) {
    const hashIdx = urlStr.indexOf('#')
    if (hashIdx !== -1) {
      const fragment = urlStr.substring(hashIdx + 1)
      if (fragment) return fullyDecode(fragment)
    }
    const qIdx = urlStr.indexOf('?')
    if (qIdx !== -1) {
      try {
        const sp = new URLSearchParams(urlStr.substring(qIdx))
        const n = sp.get('name')
        if (n) return fullyDecode(n)
      } catch { }
    }
    return 'blob'
  }
  const clean = sanitizePath(urlStr)
  if (!clean || clean.endsWith('/')) return null
  const segments = clean.split('/')
  const lastPart = segments[segments.length - 1]
  return lastPart || null
}

/**
 * 2. Extracts the full relative path including the filename.
 * Returns null if the path represents a directory or is empty.
 * Example: 'Audio%2FMusic%2Fธงไชย%2F08 หมอก.m4a' -> 'Audio/Music/ธงไชย/08 หมอก.m4a'
 * Example: '/ห#ห#/#ก#ก.m4a' -> 'ห#ห#/#ก#ก.m4a'
 *
 * @param {string} urlStr
 * @returns {string|null}
 */
export function extractRealFileNameWithPath(urlStr) {
  if (!urlStr || typeof urlStr !== 'string') return null
  if (urlStr.startsWith('blob:')) return urlStr
  const clean = sanitizePath(urlStr)
  if (!clean || clean.endsWith('/')) return null
  return clean
}

/**
 * 3. Extracts the containing directory path (without filename), ending with a trailing slash.
 * Returns empty string '' if at the root level.
 * Example: 'Audio/Music/ธงไชย/08 หมอก.m4a' -> 'Audio/Music/ธงไชย/'
 * Example: '/ห#ห#/#ก#ก.m4a' -> 'ห#ห#/'
 *
 * @param {string} urlStr
 * @returns {string}
 */
export function extractRealFilePath(urlStr) {
  if (!urlStr || typeof urlStr !== 'string') return ''
  if (urlStr.startsWith('blob:')) return ''
  const skoPattern = /^(?:https?:\/\/(?:skoservice\.akawatmor\.com|skoservice2\.akawatmor\.com|ddc1\.akawatmor\.com:56260|legacyservice\.akawatmor\.com:56260)|\/files)?\/files\/?/i
  const rawPath = urlStr.replace(skoPattern, '')
  const clean = sanitizePath(rawPath)
  if (!clean) return ''
  if (clean.endsWith('/')) return clean
  const lastSlash = clean.lastIndexOf('/')
  return lastSlash === -1 ? '' : clean.substring(0, lastSlash + 1)
}

/**
 * 4. Extracts the parent directory path 1 level up, ending with a trailing slash.
 * Returns null if already at root or cannot traverse higher.
 * Example: 'Audio/Music/ธงไชย/08 หมอก.m4a' -> 'Audio/Music/'
 * Example: '/ห#ห#/#ก#ก.m4a' -> null (since ห#ห# is top-level)
 *
 * @param {string} urlStr
 * @returns {string|null}
 */
export function extractRealParentPath(urlStr) {
  if (!urlStr || typeof urlStr !== 'string') return null
  if (urlStr.startsWith('blob:')) return null
  const dirPath = extractRealFilePath(urlStr)
  if (!dirPath) return null
  const noTrailing = dirPath.replace(/\/+$/, '')
  const lastSlash = noTrailing.lastIndexOf('/')
  return lastSlash === -1 ? null : noTrailing.substring(0, lastSlash + 1)
}

/**
 * 5. Resolves a sibling file in the same directory.
 * Example: extractSamePathFile('Audio/Music/ธงไชย/08 หมอก.m4a', 'cover.jpg') -> 'Audio/Music/ธงไชย/cover.jpg'
 * Example: extractSamePathFile('/ห#ห#/#ก#ก.m4a', 'cover.jpg') -> 'ห#ห#/cover.jpg'
 *
 * @param {string} urlStr
 * @param {string} targetFileName
 * @returns {string|null}
 */
export function extractSamePathFile(urlStr, targetFileName) {
  if (!urlStr || !targetFileName || typeof targetFileName !== 'string') return null
  if (urlStr.startsWith('blob:')) return null
  const dirPath = extractRealFilePath(urlStr)
  const cleanTarget = fullyDecode(targetFileName.trim()).replace(/^\/+/, '')
  if (!cleanTarget) return null
  return dirPath ? `${dirPath}${cleanTarget}` : cleanTarget
}

/**
 * 6. Resolves a child directory path 1 level down, ending with a trailing slash.
 * Example: extractRealChildPath('Audio/Music', 'ธงไชย') -> 'Audio/Music/ธงไชย/'
 * Example: extractRealChildPath('ห#ห#', 'sub') -> 'ห#ห#/sub/'
 *
 * @param {string} urlStr
 * @param {string} childDirName
 * @returns {string|null}
 */
export function extractRealChildPath(urlStr, childDirName) {
  if (!urlStr || !childDirName || typeof childDirName !== 'string') return null
  if (urlStr.startsWith('blob:')) return null
  const cleanChild = fullyDecode(childDirName.trim()).replace(/^\/+|\/+$/g, '')
  if (!cleanChild || cleanChild.includes('..')) return null
  const dirPath = extractRealFilePath(urlStr) || (sanitizePath(urlStr).replace(/\/+$/, '') + '/')
  const baseDir = dirPath.endsWith('/') ? dirPath : `${dirPath}/`
  return `${baseDir}${cleanChild}/`
}

/**
 * Auxiliary/Companion file extensions (.lrc, .elrc, .lyc, .lyr, .cur, .srt, .vtt)
 */
const COMPANION_EXTENSIONS = new Set(['lrc', 'elrc', 'lyc', 'lyr', 'cur', 'srt', 'vtt'])

/**
 * Checks if an extension belongs to a companion/auxiliary file type
 * @param {string} ext
 * @returns {boolean}
 */
export function isCompanionFileType(ext) {
  if (!ext || typeof ext !== 'string') return false
  return COMPANION_EXTENSIONS.has(ext.toLowerCase().trim())
}

/**
 * Searches sibling items in the same folder for the best possible matching primary media file.
 *
 * @param {string} baseName Song or media base filename (without extension)
 * @param {Array} siblingItems List of directory items ({ name: string, ... })
 * @param {boolean} [preferMidi=false] Whether to prioritize MIDI formats (.mid, .midi, .emk)
 * @returns {Object|null}
 */
export function findBestMediaMatch(baseName, siblingItems = [], preferMidi = false) {
  if (!baseName || !Array.isArray(siblingItems) || siblingItems.length === 0) return null

  const targetBase = baseName.toLowerCase()
  const midiExts = ['mid', 'midi', 'kar', 'emk']
  const audioExts = ['m4a', 'mp3', 'opus', 'flac', 'wav', 'aac', 'ogg']
  const videoExts = ['mp4', 'mkv', 'webm', 'mov']

  // For MIDI companions (like .lyc / .lyr / .cur), strictly search MIDI files (.mid, .midi, .kar, .emk)
  // to avoid accidentally matching video or unrelated audio files.
  const searchOrder = preferMidi
    ? midiExts
    : [...audioExts, ...midiExts, ...videoExts]

  for (const ext of searchOrder) {
    const found = siblingItems.find(item => {
      const name = (item.name || item.relativePath || '').toLowerCase()
      const itemFileName = extractRealFileName(name) || name
      return itemFileName.toLowerCase() === `${targetBase}.${ext}`
    })
    if (found) return found
  }

  return null
}

/**
 * Extracts clean filename from a URL or relative path (backwards-compatible wrapper).
 * @param {string} urlStr
 * @returns {string}
 */
export function extractFileName(urlStr) {
  return extractRealFileName(urlStr) || 'file'
}

/**
 * Extracts lowercase file extension from a URL or raw path.
 *
 * @param {string} urlStr
 * @returns {string}
 */
export function getExtension(urlStr) {
  if (!urlStr) return ''
  const fileName = extractRealFileName(urlStr) || ''
  const lower = fileName.toLowerCase()

  if (lower.endsWith('.stem.mp4') || lower.endsWith('.stem.m4a')) {
    return 'stem.mp4'
  }

  const lastDot = fileName.lastIndexOf('.')
  if (lastDot === -1 || lastDot === fileName.length - 1) return ''

  const extMatch = fileName.slice(lastDot + 1).match(/^[a-zA-Z0-9]+/)
  return extMatch ? extMatch[0].toLowerCase() : ''
}

/**
 * Extracts the parent directory URL with a trailing slash from a file URL or path.
 *
 * @param {string} urlStr
 * @returns {string}
 */
export function getParentFolderUrl(urlStr) {
  if (!urlStr || typeof urlStr !== 'string') return ''
  const clean = urlStr.split('?')[0].replace(/\/+$/, '')
  const lastSlash = clean.lastIndexOf('/')
  return lastSlash !== -1 ? clean.substring(0, lastSlash + 1) : ''
}

/**
 * Returns all compatible viewer types for a file, and the recommended default type.
 *
 * @param {string} url
 * @param {boolean} [isFolder=false]
 * @returns {{ types: string[], defaultType: string }}
 */
export function getCompatibleTypes(url, isFolder = false) {
  if (!url) return { types: [], defaultType: '' }

  const decodedUrl = fullyDecode(url)

  if (isFolder || decodedUrl.endsWith('/')) {
    return { types: ['folder'], defaultType: 'folder' }
  }

  const ext = getExtension(decodedUrl)

  const finalize = (result) => {
    const types = result.types ? [...result.types] : []
    if (!types.includes('code')) types.push('code')
    if (!types.includes('hex')) types.push('hex')
    return { types, defaultType: result.defaultType || types[0] }
  }

  // Stem multi-track audio
  if (ext === 'stem' || ext === 'stem.mp4' || ext === 'stem.m4a') {
    return finalize({ types: ['stem', 'audio', 'video'], defaultType: 'stem' })
  }

  // Advance Audio & Surround formats (MKA, AC3 Dolby Digital, EAC3 / EC3 Dolby Digital Plus)
  if (['mka', 'ac3', 'eac3', 'ec3'].includes(ext)) {
    return finalize({ types: ['advance_audio', 'audio', 'video'], defaultType: 'advance_audio' })
  }

  // Opus multi-channel & advanced audio
  if (ext === 'opus') {
    return finalize({ types: ['audio', 'advance_audio'], defaultType: 'audio' })
  }

  // Playlist formats (M3U, M3U8, PLS, CUE, XSPF, ASX, WPL)
  if (['m3u', 'm3u8', 'pls', 'cue', 'xspf', 'asx', 'wpl'].includes(ext)) {
    if (ext === 'm3u8') {
      return finalize({ types: ['playlist', 'video', 'audio'], defaultType: 'playlist' })
    }
    return finalize({ types: ['playlist', 'audio', 'video'], defaultType: 'playlist' })
  }

  // Video formats (can also be listened to as audio)
  if (['mp4', 'webm', 'mkv', 'mov', 'avi', 'm4v', 'ogv', 'ts', 'flv'].includes(ext)) {
    return finalize({ types: ['video', 'audio'], defaultType: 'video' })
  }

  // Chiptune / Music Tracker formats (MOD, XM, S3M, IT, MO3, etc.)
  if (['mod', 'xm', 's3m', 'it', 'mo3', 'mtm', '669', 'far', 'umx'].includes(ext)) {
    return finalize({ types: ['tracker', 'audio'], defaultType: 'tracker' })
  }

  // MIDI & Karaoke formats (Standard MIDI, KAR, eXtreme EMK, RMI)
  if (['mid', 'midi', 'kar', 'rmi', 'emk'].includes(ext)) {
    return finalize({ types: ['midi', 'audio'], defaultType: 'midi' })
  }

  // Karaoke Companion files (NCN LYC, LYR, CUR) - companion for MIDI, text/data by nature
  if (['lyc', 'lyr', 'cur'].includes(ext)) {
    return finalize({ types: ['midi', 'code'], defaultType: 'code' })
  }

  // Synced Lyrics files (LRC, ELRC) - text-based timing and lyrics
  if (['lrc', 'elrc'].includes(ext)) {
    return finalize({ types: ['code', 'cdg', 'audio'], defaultType: 'code' })
  }

  // CD+G Karaoke Graphics (CDG)
  if (['cdg'].includes(ext)) {
    return finalize({ types: ['cdg', 'audio', 'code'], defaultType: 'cdg' })
  }

  // SoundFont Bank (SF2, SFZ, SF3)
  if (['sf2', 'sf3', 'sfz'].includes(ext)) {
    return finalize({ types: ['soundfont'], defaultType: 'soundfont' })
  }

  // Presentation (PPTX, PPT, POTX, ODP)
  if (['pptx', 'ppt', 'potx', 'odp'].includes(ext)) {
    return finalize({ types: ['office', 'pptx', 'code'], defaultType: 'office' })
  }

  // 3D Models (GLTF, GLB, OBJ, STL, PLY, FBX)
  if (['gltf', 'glb', 'obj', 'stl', 'ply', 'fbx'].includes(ext)) {
    return finalize({ types: ['model3d'], defaultType: 'model3d' })
  }

  // E-Book & Comic (EPUB, MOBI, AZW3, CBR, CBZ)
  if (['epub', 'mobi', 'azw3', 'cbr', 'cbz'].includes(ext)) {
    return finalize({ types: ['ebook'], defaultType: 'ebook' })
  }

  // ANSI Art & Retro ASCII (ANS, NFO, DIZ, ASC)
  if (['ans', 'nfo', 'diz', 'asc'].includes(ext)) {
    return finalize({ types: ['ansi', 'code'], defaultType: 'ansi' })
  }

  // Macromedia / Adobe Flash Animation & Games (SWF)
  if (ext === 'swf') {
    return finalize({ types: ['flash'], defaultType: 'flash' })
  }

  // Windows Animated Cursor (ANI, CUR)
  if (['ani', 'cur'].includes(ext)) {
    return finalize({ types: ['cursor', 'image'], defaultType: 'cursor' })
  }

  // Console Video Game Chiptune (SNES SPC, MegaDrive VGM, NES NSF, C64 SID, GBS, VOC)
  if (['spc', 'vgm', 'vgz', 'nsf', 'sid', 'gbs', 'voc'].includes(ext)) {
    return finalize({ types: ['chiptune_console', 'audio'], defaultType: 'chiptune_console' })
  }

  // DOOM & Classic Game Asset Packages (WAD, GRP, BSP)
  if (['wad', 'grp', 'bsp'].includes(ext)) {
    return finalize({ types: ['wad', 'archive'], defaultType: 'wad' })
  }

  // Autodesk Animator (FLI, FLC)
  if (['fli', 'flc'].includes(ext)) {
    return finalize({ types: ['video', 'image'], defaultType: 'video' })
  }

  // Subtitles & Closed Captions (SRT, VTT, ASS, SSA, SUB)
  if (['srt', 'vtt', 'ass', 'ssa', 'sub'].includes(ext)) {
    return finalize({ types: ['subtitle', 'code'], defaultType: 'subtitle' })
  }

  // GIS & Map Data (GEOJSON, KML, GPX, TOPOJSON)
  if (['geojson', 'kml', 'gpx', 'topojson'].includes(ext)) {
    return finalize({ types: ['geo', 'code'], defaultType: 'geo' })
  }

  // Design & RAW Graphics (PSD, TIFF, TIF, HDR, EXR)
  if (['psd', 'tiff', 'tif', 'hdr', 'exr'].includes(ext)) {
    return finalize({ types: ['design', 'image'], defaultType: 'design' })
  }

  // Audio formats
  if (['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac', 'opus', 'wma', 'mka', 'ac3', 'eac3', 'ec3'].includes(ext)) {
    return finalize({ types: ['audio', 'advance_audio'], defaultType: 'audio' })
  }

  // HTML Web Pages
  if (['html', 'htm', 'xhtml'].includes(ext)) {
    return finalize({ types: ['html', 'code'], defaultType: 'html' })
  }

  // Markdown Documents
  if (['md', 'markdown', 'mdown', 'mkd'].includes(ext)) {
    return finalize({ types: ['markdown', 'code'], defaultType: 'markdown' })
  }

  // Casio Calculator BASIC / Memory Data (.g1m, .g2m)
  if (['g1m', 'g2m'].includes(ext)) {
    return finalize({ types: ['g1m', 'code'], defaultType: 'g1m' })
  }

  // Database & Parquet formats (SQLite, Parquet)
  if (['db', 'sqlite', 'sqlite3', 'parquet'].includes(ext)) {
    return finalize({ types: ['database'], defaultType: 'database' })
  }

  // Typography & Font formats (TTF, OTF, WOFF, WOFF2, EOT)
  if (['ttf', 'otf', 'woff', 'woff2', 'eot'].includes(ext)) {
    return finalize({ types: ['font'], defaultType: 'font' })
  }

  // Vector Graphics (SVG, SVGZ) - Default to Image Viewer with Code Viewer (Markup) support
  if (['svg', 'svgz'].includes(ext)) {
    return finalize({ types: ['image', 'code'], defaultType: 'image' })
  }

  // Disk Images & ISO (Sector-by-sector memory-safe parsing & v86 x86 emulation)
  if (['iso', 'img', 'ima'].includes(ext)) {
    return finalize({ types: ['disk', 'archive'], defaultType: 'disk' })
  }

  // Universal Archive & Compressed formats (ZIP, RAR, 7Z, TAR, GZ, BZ2, XZ, CAB, etc.)
  if (['zip', 'rar', '7z', 'tar', 'gz', 'tgz', 'bz2', 'tbz2', 'xz', 'txz', 'zst', 'cab', 'ar', 'cpio', 'xar', 'lzh', 'lha'].includes(ext)) {
    return finalize({ types: ['archive'], defaultType: 'archive' })
  }

  // Jupyter Notebook (.ipynb)
  if (['ipynb'].includes(ext)) {
    return finalize({ types: ['notebook', 'code'], defaultType: 'notebook' })
  }

  // Source Code, Config, Script & Text Formats
  if ([
    'txt', 'text', 'log', 'out', 'err', 'ans', 'nfo', 'asc', 'diz', 'me', '1st',
    'json', 'jsonc', 'json5', 'lock', 'env', 'ini', 'cfg', 'conf', 'config', 'properties', 'prefs', 'inf', 'reg',
    'py', 'pyw', 'pyi', 'pyx', 'pxd', 'rpy', 'sage', 'pyde', 'pyt',
    'c', 'cpp', 'h', 'hpp', 'cc', 'cxx', 'hh', 'hxx', 'java', 'cs', 'go', 'rs',
    'js', 'mjs', 'cjs', 'ts', 'mts', 'cts', 'vue', 'svelte', 'jsx', 'tsx', 'css', 'scss', 'sass', 'less',
    'xml', 'yaml', 'yml', 'toml', 'sh', 'bash', 'zsh', 'fish', 'ps1', 'psm1', 'bat', 'cmd',
    'sql', 'php', 'rb', 'lua', 'swift', 'kt', 'kts', 'dart', 'r', 'm', 'pl', 'pm', 't',
    'graphql', 'gql', 'proto', 'asm', 's', 'tex', 'latex', 'bib', 'diff', 'patch', 'dockerfile', 'makefile'
  ].includes(ext)) {
    if (ext === 'json') {
      return finalize({ types: ['code', 'database'], defaultType: 'code' })
    }
    return finalize({ types: ['code'], defaultType: 'code' })
  }

  // Binary & Executable Formats
  if (['bin', 'dat', 'exe', 'dll', 'so', 'dylib', 'o', 'class', 'iso', 'rom', 'hex', 'pyd'].includes(ext)) {
    return finalize({ types: ['hex', 'code'], defaultType: 'hex' })
  }

  // PDF
  if (['pdf'].includes(ext)) {
    return finalize({ types: ['pdf'], defaultType: 'pdf' })
  }

  // Dot-Matrix Printer Font & Spool Files (PRN, P24, FON)
  if (['prn', 'p24', 'fon'].includes(ext)) {
    return finalize({ types: ['prn_p24', 'cuwriter', 'code'], defaultType: 'prn_p24' })
  }

  // Vintage Thai Word Processors (CU-Writer / Chula Word .CW, CU-Writer for Windows .CWW)
  if (['cw', 'cww', 'cw3'].includes(ext)) {
    return finalize({ types: ['cuwriter', 'code'], defaultType: 'cuwriter' })
  }

  // Vintage DOS Word Processors (WordStar, WordPerfect)
  if (['ws', 'wsd', 'ws4', 'ws7', 'wpd', 'wp', 'wp5', 'wp6'].includes(ext)) {
    return finalize({ types: ['retro_doc', 'code'], defaultType: 'retro_doc' })
  }

  // Word Document (DOCX, legacy DOC, DOTX, RTF)
  if (['docx', 'dotx', 'doc', 'rtf'].includes(ext)) {
    if (ext === 'doc') {
      return finalize({ types: ['office', 'docx', 'cuwriter', 'code'], defaultType: 'office' })
    }
    return finalize({ types: ['office', 'docx', 'code'], defaultType: 'office' })
  }

  // Excel / Spreadsheet & Lotus 1-2-3 (XLSX, legacy XLS, CSV, ODS, WK1, WK3, WKS, 123)
  if (['xlsx', 'xls', 'csv', 'ods', 'wk1', 'wk3', 'wks', '123'].includes(ext)) {
    if (ext === 'csv') {
      return finalize({ types: ['xlsx', 'office', 'database', 'code'], defaultType: 'xlsx' })
    }
    return finalize({ types: ['office', 'xlsx', 'code'], defaultType: 'office' })
  }

  // Image formats (Vector SVG/SVGZ and Bitmaps)
  if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp', 'svg', 'svgz', 'ico', 'avif', 'apng', 'jfif'].includes(ext)) {
    return finalize({ types: ['image'], defaultType: 'image' })
  }

  // Fallback for files without extension: include cuwriter so DOS documents without extension can be opened!
  if (!ext) {
    return finalize({ types: ['cuwriter', 'code'], defaultType: 'code' })
  }

  // Fallback for ANY unknown file:
  // Default to 'code' (Source Code & Text) so ANY text file can be opened!
  return finalize({ types: ['code'], defaultType: 'code' })
}

/**
 * Fetches directory list from SKOService public files API (autoindex json)
 * @param {string} [subpath='']
 * @returns {Promise<{ items: Array<{ name: string, type: 'directory'|'file', mtime?: string, size?: number }>, currentPath: string }>}
 */
export async function fetchSkoDirectory(subpath = '') {
  const isDev = Boolean(typeof import.meta !== 'undefined' && import.meta.env?.DEV)
  const base = isDev ? '/files/' : SKO_FILES_BASE
  const cleanPath = subpath ? fullyDecode(subpath).replace(/^(?:https?:\/\/[^\/]+)?\/files\/?/i, '').replace(/^\/+|\/+$/g, '') : ''
  const targetUrl = cleanPath
    ? `${base}${cleanPath.split('/').map(encodeURIComponent).join('/')}/?type=json`
    : `${base}?type=json`

  try {
    const res = await fetch(targetUrl)
    if (!res.ok) {
      throw new Error(`HTTP error ${res.status}`)
    }
    const data = await res.json()
    if (!Array.isArray(data)) {
      throw new Error('Invalid directory format returned from server')
    }

    const sorted = [...data].sort((a, b) => {
      if (a.type === 'directory' && b.type !== 'directory') return -1
      if (a.type !== 'directory' && b.type === 'directory') return 1
      return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' })
    })

    return {
      items: sorted,
      currentPath: cleanPath
    }
  } catch (err) {
    console.error('[fetchSkoDirectory] Error:', err)
    throw err
  }
}

/**
 * Format bytes to readable string (KB, MB, GB)
 * @param {number} bytes
 * @returns {string}
 */
export function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}
